const excludeEndpoints = [
  'bff/upload',
  'bff/poll/classify-result',
] as const;

const API_PREFIX = '/eclaims/api/v1/';

/**
 * Returns whether the given URL should be excluded based on the rules.
 * - 'bff/upload' → true only when exact path (no numeric ID after)
 * - 'bff/poll/classify-result' → true even with a numeric ID after
 */
export function isExcludedEndpoint(url: string): boolean {
  let pathname: string;

  try {
    pathname = new URL(url).pathname;
  } catch {
    // Not a valid absolute URL: return false (or adjust if you need to support relative URLs)
    return false;
  }

  // Find the part after the API prefix
  const prefixIndex = pathname.indexOf(API_PREFIX);
  if (prefixIndex === -1) return false;

  const afterPrefix = pathname.slice(prefixIndex + API_PREFIX.length);

  // Normalize and split segments
  const segments = afterPrefix.split('/').filter(Boolean); // removes empty '' from leading/trailing slashes

  // Expect at least: ['bff', ...]
  if (segments[0] !== 'bff') return false;

  // Build the first two parts to compare with excludeEndpoints
  const firstTwo = segments.slice(0, 2).join('/'); // e.g., 'bff/upload' or 'bff/poll'
  const firstThree = segments.slice(0, 3).join('/'); // e.g., 'bff/poll/classify-result'

  // Handle 'bff/upload'
  if (firstTwo === 'bff/upload') {
    // exact match: just ['bff', 'upload'] → true
    if (segments.length === 2) return true;

    // if next segment is numeric → false, otherwise still treat as not excluded
    const next = segments[2];
    return !(isAllDigits(next)); // numeric means NOT excluded → false
  }

  // Handle 'bff/poll/classify-result' (always excluded even with ID)
  if (firstThree === 'bff/poll/classify-result') {
    return true;
  }

  return false;
}

function isAllDigits(value: string | undefined): boolean {
  return !!value && /^\d+$/.test(value);
}